import { isPending, type MetricValue, type PendingMetric } from '@fleetcore/shared'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '../lib/cn'
import { Skeleton } from './ui/skeleton'

const AVAILABILITY_KEYS: Record<PendingMetric['availability'], string> = {
  pending_telemetry: 'common:metric.pendingTelemetry',
  not_supported_by_vehicle: 'common:metric.notSupported',
  insufficient_history: 'common:metric.insufficientHistory',
}

export interface MetricTileProps {
  label: string
  hint?: string
  metric: MetricValue<number>
  /** Turns an available value into display text — locale-aware, unit-aware. */
  format: (value: number) => string
  icon?: LucideIcon
  isLoading?: boolean
  className?: string
  /**
   * Drop the hint line below `sm`. For tiles laid out two to a row on a phone, where a
   * one-line hint is cut to three words — worse than no hint. The label carries it.
   */
  compactOnPhone?: boolean
}

/**
 * A KPI tile that refuses to invent a number.
 *
 * An unmeasured metric renders "En attente de télémétrie", never 0. A fleet
 * manager reading "0 L" concludes the fleet burned no fuel today; reading
 * "en attente de télémétrie" concludes the platform has not measured it yet.
 * Those are opposite operational decisions, so the distinction is enforced here
 * rather than left to each caller.
 */
export function MetricTile({
  label,
  hint,
  metric,
  format,
  icon: Icon,
  isLoading = false,
  className,
  compactOnPhone = false,
}: MetricTileProps) {
  const hintClass = compactOnPhone ? 'hidden sm:block' : undefined
  const { t, i18n } = useTranslation()
  const pending = isPending(metric)

  const pendingText = pending
    ? // A PendingMetric may carry its own reason key; fall back to the generic
      // wording for the availability class when that key is not in the catalog.
      metric.reasonKey && i18n.exists(metric.reasonKey)
      ? t(metric.reasonKey)
      : t(AVAILABILITY_KEYS[metric.availability])
    : null

  return (
    <div
      aria-busy={isLoading || undefined}
      className={cn(
        'flex min-w-0 flex-col gap-2 rounded-lg border border-border bg-surface p-4 shadow-card',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p
          className={cn(
            'text-xs font-medium uppercase tracking-wide text-foreground-muted',
            // Two narrow tiles side by side: let the label wrap rather than lose its end.
            compactOnPhone ? 'break-words sm:truncate' : 'truncate',
          )}
        >
          {label}
        </p>
        {Icon ? <Icon aria-hidden className="h-4 w-4 shrink-0 text-foreground-subtle" /> : null}
      </div>

      {isLoading ? (
        <>
          <Skeleton className="h-7 w-20" />
          <Skeleton className="h-3 w-32" />
        </>
      ) : pending ? (
        <>
          <p
            className={cn(
              'font-medium text-foreground-subtle',
              compactOnPhone ? 'text-xs leading-snug sm:text-sm sm:leading-7' : 'text-sm leading-7',
            )}
          >
            {pendingText}
          </p>
          <p className={cn('text-xs text-foreground-subtle', hintClass)}>{hint}</p>
        </>
      ) : (
        <>
          <p className="text-2xl font-semibold tabular-nums text-foreground">
            {format(metric.value)}
          </p>
          {hint ? (
            <p className={cn('truncate text-xs text-foreground-subtle', hintClass)}>{hint}</p>
          ) : null}
        </>
      )}
    </div>
  )
}
